import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Save, Zap, FileText, CheckCircle2, XCircle, ShieldCheck, Power, PlugZap, AlertTriangle, ChevronDown, Trash2 } from "lucide-react";
import TaxPortalLookup from "@/components/TaxPortalLookup";

const CAP_LABELS = {
  e_invoice: "e-Fatura", e_archive: "e-Arşiv", e_despatch: "e-İrsaliye", e_smm: "e-SMM",
  e_producer: "e-Müstahsil", e_reconciliation: "e-Mutabakat", balance_query: "Bakiye/Kontör",
  taxpayer_list: "Mükellef Sorgu", incoming: "Gelen Belgeler",
};
const ST_LABELS = { auth: "Giriş (Auth)", einvoice: "e-Fatura", earchive: "e-Arşiv", edespatch: "e-İrsaliye", connector: "Servis (tek WCF)", taxpayer: "Mükellef" };

export default function EDocWizardV2({ isAdmin }) {
  const [data, setData] = useState(null);
  const [provider, setProvider] = useState("");
  const [env, setEnv] = useState("test");
  const [form, setForm] = useState({});
  const [secrets, setSecrets] = useState({});
  const [overrides, setOverrides] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [busy, setBusy] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [vknVerifiedAt, setVknVerifiedAt] = useState("");
  const [kontor, setKontor] = useState({ balance: "", threshold: "" });
  const [usage, setUsage] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/edoc/v2/providers");
      setData(data);
      if (data.providers?.length) setProvider((current) => current || data.providers[0].manifest.provider_code);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "E-Belge verileri yüklenemedi"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = useMemo(() => data?.providers?.find((p) => p.manifest.provider_code === provider), [data, provider]);
  const manifest = current?.manifest;
  const conn = current?.connections?.[env] || null;

  useEffect(() => {
    if (!manifest) return;
    setTestResult(null);
    const pub = conn?.public_config || {};
    const f = {};
    manifest.config_fields.filter((x) => !x.secret).forEach((x) => { f[x.key] = pub[x.key] || ""; });
    setForm(f);
    setSecrets({});
    setVknVerifiedAt("");
    setOverrides(conn?.endpoint_overrides || {});
    setKontor({ balance: conn?.kontor_balance ?? "", threshold: conn?.kontor_threshold ?? "" });
    setEnv((e) => (manifest.environments.includes(e) ? e : "test"));
  }, [provider, env, manifest, conn]);

  useEffect(() => {
    if (!conn) { setUsage(null); return; }
    api.get(`/edoc/v2/connection/${provider}/${env}/kontor-usage`)
      .then((r) => setUsage(r.data)).catch(() => setUsage(null));
    /* eslint-disable-next-line */
  }, [provider, env, conn?.id, conn?.kontor_balance]);

  if (!data) return <div className="rounded-lg bg-white p-8 text-center ring-1 ring-inset ring-slate-200"><Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-green" /></div>;

  const groups = {};
  (manifest?.config_fields || []).forEach((x) => { (groups[x.group] = groups[x.group] || []).push(x); });
  const endpoints = manifest?.endpoint_definitions?.[env] || [];
  const statusColor = { AKTİF: "bg-emerald-50 text-emerald-700 ring-emerald-200", "TEST BAŞARILI": "bg-sky-50 text-sky-700 ring-sky-200", "TEST BAŞARISIZ": "bg-red-50 text-red-700 ring-red-200", "CANLI AKTİVASYON BEKLİYOR": "bg-amber-50 text-amber-700 ring-amber-200" };
  const stLabel = conn?.status_label || "TASLAK";

  const fetchCompany = async () => {
    try {
      const { data } = await api.get("/settings/company-profile");
      if (!data || !Object.values(data).some((v) => (v || "").toString().trim())) {
        toast.error("Firma bilgisi tanımlı değil — Ayarlar > Firma Bilgileri'nden bir kez girin.");
        return;
      }
      const fields = manifest?.config_fields || [];
      const patch = {};
      const setIf = (pred, val) => {
        if (!val) return;
        const f = fields.find((x) => !x.secret && pred((x.key || "").toLowerCase()));
        if (f) patch[f.key] = val;
      };
      setIf((k) => k.includes("vkn") || k.includes("tckn") || k.includes("vergi_no") || k.includes("taxno") || k.includes("tax_no"), data.tax_no);
      setIf((k) => k.includes("unvan") || k.includes("title") || (k.includes("firma") && k.includes("ad")) || (k.includes("company") && k.includes("name")) || k === "company_title" || k === "name", data.legal_name || data.name);
      setIf((k) => k.includes("vergi_daire") || k.includes("tax_office") || k.includes("taxoffice") || k.includes("vd"), data.tax_office);
      setIf((k) => k.includes("email") || k.includes("eposta") || k.includes("mail"), data.email);
      setIf((k) => k.includes("phone") || k.includes("tel") || k.includes("gsm"), data.phone);
      setIf((k) => k.includes("adres") || k.includes("address"), data.address);
      setIf((k) => k === "il" || k.includes("city") || k.includes("province") || k.includes("sehir"), data.province);
      setIf((k) => k === "ilce" || k.includes("district"), data.district);
      if (Object.keys(patch).length === 0) { toast.info("Bu sağlayıcı için eşleşen firma alanı bulunamadı"); return; }
      setForm((f) => ({ ...f, ...patch }));
      if (Object.keys(patch).some((k) => (k || "").toLowerCase().includes("vkn"))) setVknVerifiedAt("");
      toast.success(`Firma bilgileri dolduruldu (${Object.keys(patch).length} alan)`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Firma bilgisi getirilemedi"); }
  };

  const doSave = async () => {
    setBusy("save");
    try {
      const secretPayload = {};
      Object.entries(secrets).forEach(([k, v]) => { if (v && v.trim()) secretPayload[k] = v.trim(); });
      const { data: res } = await api.post("/edoc/v2/connection", { provider, environment: env, public_config: form, secrets: secretPayload, endpoint_overrides: overrides });
      toast.success("Bağlantı bilgileri kaydedildi");
      setSecrets({});
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setBusy(""); }
  };
  const doTest = async () => {
    setBusy("test"); setTestResult(null);
    try {
      const { data: res } = await api.post(`/edoc/v2/connection/${provider}/${env}/test`);
      setTestResult(res);
      res.ok ? toast.success(res.summary || "Bağlantı başarılı") : toast.error(res.summary || "Bağlantı başarısız");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Test yapılamadı"); }
    finally { setBusy(""); }
  };
  const doActivate = async (active) => {
    setBusy("activate");
    try {
      await api.post(`/edoc/v2/connection/${provider}/${env}/${active ? "activate" : "deactivate"}`);
      toast.success(active ? "Bağlantı aktif edildi" : "Bağlantı devre dışı bırakıldı");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
    finally { setBusy(""); }
  };
  const doLiveApprove = async (approved) => {
    setBusy("live");
    try {
      await api.post(`/edoc/v2/connection/${provider}/${env}/live-approve`, { approved });
      toast.success(approved ? "Canlı gönderim onaylandı" : "Canlı onay kaldırıldı");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
    finally { setBusy(""); }
  };
  const doDelete = async () => {
    if (!window.confirm(`${manifest?.display_name} (${env.toUpperCase()}) bağlantısı silinsin mi? Kayıtlı bilgiler kaldırılacaktır.`)) return;
    setBusy("delete");
    try {
      await api.delete(`/edoc/v2/connection/${provider}/${env}`);
      toast.success("Entegratör bağlantısı silindi");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Silinemedi"); }
    finally { setBusy(""); }
  };
  const saveKontor = async () => {
    setBusy("kontor");
    try {
      await api.post(`/edoc/v2/connection/${provider}/${env}/kontor`, {
        balance: kontor.balance === "" ? null : Number(kontor.balance),
        threshold: kontor.threshold === "" ? null : Number(kontor.threshold),
      });
      toast.success("Kontör ayarı kaydedildi");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi"); }
    finally { setBusy(""); }
  };
  const queryBalance = async () => {
    setBusy("balance");
    try {
      const { data: res } = await api.post(`/edoc/v2/connection/${provider}/${env}/balance-query`);
      if (res.unsupported) toast.info(res.message);
      else if (res.ok) toast.success(res.message || "Bakiye alındı");
      else toast.error(res.message || "Bakiye alınamadı");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Sorgu başarısız"); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-lg bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="edoc-v2-wizard">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-green"><FileText className="h-5 w-5 text-white" /></div>
          <div>
            <p className="overline">Sağlayıcı-bağımsız</p>
            <h2 className="font-heading text-lg font-bold text-slate-900">E-Belge Bağlantı Sihirbazı</h2>
          </div>
        </div>
        <span data-testid="edoc-v2-status" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ring-1 ring-inset ${statusColor[stLabel] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>
          <PlugZap className="h-3.5 w-3.5" /> {stLabel}
        </span>
      </div>

      {/* ADIM 1 · Entegratör */}
      <div className="mb-3">
        <Label className="overline">ADIM 1 · Entegratör</Label>
        <div className="mt-1.5 flex flex-wrap gap-2" data-testid="edoc-v2-providers">
          {data.providers.map((p) => {
            const active = provider === p.manifest.provider_code;
            const configured = !!(p.connections?.test || p.connections?.live);
            const beta = p.manifest.integration_status === "beta";
            return (
              <button key={p.manifest.provider_code} type="button" data-testid={`edoc-v2-provider-${p.manifest.provider_code}`}
                onClick={() => setProvider(p.manifest.provider_code)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold ring-1 ring-inset transition-colors ${active ? "bg-brand-green text-white ring-brand-green" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                {configured && <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-white" : "bg-emerald-500"}`} title="Yapılandırıldı" />}
                {p.manifest.display_name}
                {beta && <span className={`rounded px-1 py-0.5 text-[9px] font-bold ${active ? "bg-white/25 text-white" : "bg-amber-100 text-amber-700"}`}>BETA</span>}
              </button>
            );
          })}
        </div>
      </div>

      {manifest && (<>
        {/* ADIM 2 · Ortam */}
        <div className="mb-3">
          <Label className="overline">ADIM 2 · Ortam</Label>
          <div className="mt-1.5 flex gap-2" data-testid="edoc-v2-envs">
            {manifest.environments.map((e) => (
              <button key={e} type="button" data-testid={`edoc-v2-env-${e}`} onClick={() => setEnv(e)}
                className={`rounded-md px-4 py-1.5 text-sm font-semibold ring-1 ring-inset transition-colors ${env === e ? (e === "live" ? "bg-brand-orange text-white ring-brand-orange" : "bg-brand-green text-white ring-brand-green") : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                {e === "live" ? "CANLI" : "TEST"}
              </button>
            ))}
          </div>
          {env === "live" && (
            <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 p-2.5 text-[12px] text-amber-800 ring-1 ring-inset ring-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>CANLI ortam: aktivasyon sonrası belgeler <b>gerçek entegratör ve GİB</b> sistemine gönderilebilir. Ayrıca Yönetici canlı onayı gerekir.</span>
            </div>
          )}
        </div>

        {/* Yetenekler */}
        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="edoc-v2-caps">
          {manifest.supported_capabilities.map((c) => (
            <span key={c} className="rounded bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">{CAP_LABELS[c] || c}</span>
          ))}
        </div>

        {manifest.integration_status === "beta" && (
          <div className="mb-4 flex items-start gap-2 rounded-md bg-sky-50 p-2.5 text-[12px] text-sky-800 ring-1 ring-inset ring-sky-200" data-testid="edoc-v2-beta-note">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span><b>Beta entegratör (altyapı hazır).</b> Bilgileri kaydedebilir ve servis adresine <b>bağlanabilirliği test</b> edebilirsiniz. Servis adreslerini "Gelişmiş" bölümünden girin. Gerçek belge gönderimi şu an <b>EDM Bilişim / İzibiz</b>'de aktiftir; bu entegratörde yakında açılacaktır.</span>
          </div>
        )}

        {/* ADIM 3-4 · Firma & Kimlik (manifest'ten dinamik) */}
        {Object.entries(groups).map(([grp, fields]) => (
          <div key={grp} className="mb-3">
            <Label className="overline">{grp}</Label>
            <div className="mt-1.5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f.key} className={f.type === "number" ? "" : ""}>
                  <Label className="text-xs text-slate-600">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
                  {f.secret ? (
                    <Input data-testid={`edoc-v2-field-${f.key}`} type="password" autoComplete="new-password"
                      value={secrets[f.key] || ""} onChange={(e) => setSecrets({ ...secrets, [f.key]: e.target.value })}
                      placeholder={conn?.secrets_present?.[f.key] ? "•••• kayıtlı — değiştirmek için yazın" : (f.placeholder || f.label)}
                      className="mt-1" />
                  ) : (
                    <Input data-testid={`edoc-v2-field-${f.key}`} type={f.type === "number" ? "number" : "text"}
                      value={form[f.key] || ""} onChange={(e) => { setForm({ ...form, [f.key]: e.target.value }); if (f.key === "company_vkn") setVknVerifiedAt(""); }}
                      placeholder={f.placeholder} className="mt-1" />
                  )}
                  {f.help && <p className="mt-0.5 text-[10px] text-slate-400">{f.help}</p>}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Firma VKN/TCKN · Doğrula & Getir */}
        <div className="mb-2 flex items-center justify-between rounded-md bg-sky-50 px-3 py-2 ring-1 ring-inset ring-sky-100">
          <span className="flex items-center gap-1.5 text-xs text-sky-700"><FileText className="h-4 w-4" /> Kendi firma bilgilerinizi tek kaynaktan doldurun (Ayarlar &gt; Firma Bilgileri).</span>
          <Button type="button" data-testid="edoc-v2-fetch-company" onClick={fetchCompany} size="sm" variant="outline" className="h-8 border-sky-300 bg-white text-sky-700 hover:bg-sky-100">Firma Getir</Button>
        </div>
        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TaxPortalLookup taxNo={form.company_vkn} type={(form.company_vkn || "").trim().length === 11 ? "individual" : "corporate"}
            name={form.company_title || form.company_name || form.unvan || ""}
            onVerified={(ts) => setVknVerifiedAt(ts)} verifiedAt={vknVerifiedAt} testid="edoc-v2-tax-portal" />
        </div>

        {/* ADIM 6 · Servis adresleri (Gelişmiş) */}
        <button type="button" data-testid="edoc-v2-advanced-toggle" onClick={() => setShowAdvanced((v) => !v)}
          className="mt-1 flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
          <ChevronDown className={`h-4 w-4 transition-transform ${showAdvanced ? "rotate-180" : ""}`} /> Gelişmiş: Servis Adresleri (WSDL)
        </button>
        {showAdvanced && (
          <div className="mt-2 space-y-2 rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200" data-testid="edoc-v2-endpoints">
            {endpoints.map((e) => {
              const locked = e.locked;
              const val = overrides[e.service_type] ?? e.endpoint_url;
              return (
                <div key={e.service_type}>
                  <Label className="text-[11px] text-slate-500">{ST_LABELS[e.service_type] || e.service_type} · {e.transport}{locked && " (sabit)"}{e.requires_override && " — entegratörden alın"}</Label>
                  <Input data-testid={`edoc-v2-endpoint-${e.service_type}`} value={val || ""} readOnly={locked || !isAdmin}
                    onChange={(ev) => setOverrides({ ...overrides, [e.service_type]: ev.target.value })}
                    placeholder={e.requires_override ? "https://... (İzibiz tarafından verilir)" : ""}
                    className={`mt-1 font-mono text-xs ${locked || !isAdmin ? "bg-slate-100 text-slate-500" : ""}`} />
                </div>
              );
            })}
            {!isAdmin && <p className="text-[10px] text-amber-600">Servis adresi override yalnızca Yönetici tarafından değiştirilebilir.</p>}
          </div>
        )}

        {/* ADIM 7-8 · Test & Aktivasyon */}
        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="edoc-v2-save" onClick={doSave} disabled={!!busy} className="bg-brand-green text-white hover:bg-brand-green-600">
            {busy === "save" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Kaydet
          </Button>
          <Button data-testid="edoc-v2-test" onClick={doTest} disabled={!!busy} variant="outline">
            {busy === "test" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Zap className="mr-1.5 h-4 w-4" />} Bağlantıyı Test Et
          </Button>
          {conn?.active ? (
            <Button data-testid="edoc-v2-deactivate" onClick={() => doActivate(false)} disabled={!!busy} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
              <Power className="mr-1.5 h-4 w-4" /> Devre Dışı Bırak
            </Button>
          ) : (
            <Button data-testid="edoc-v2-activate" onClick={() => doActivate(true)} disabled={!!busy} className="bg-brand-orange text-white hover:bg-orange-600">
              <ShieldCheck className="mr-1.5 h-4 w-4" /> Aktifleştir
            </Button>
          )}
          {env === "live" && isAdmin && (
            <Button data-testid="edoc-v2-live-approve" onClick={() => doLiveApprove(!conn?.live_approved)} disabled={!!busy} variant="outline"
              className={conn?.live_approved ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700 hover:bg-amber-50"}>
              {conn?.live_approved ? "Canlı Onay: AÇIK" : "Canlı Gönderimi Onayla"}
            </Button>
          )}
          {conn && (
            <Button data-testid="edoc-v2-delete" onClick={doDelete} disabled={!!busy} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
              {busy === "delete" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />} Sil
            </Button>
          )}
        </div>

        {conn && (
          <div className="mt-4 rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200" data-testid="edoc-v2-kontor">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">Kontör Takibi</p>
              {conn.kontor_low && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700" data-testid="edoc-v2-kontor-low">DÜŞÜK KONTÖR</span>}
            </div>
            <p className="mt-0.5 text-[11px] text-slate-400">Kalan kontör uyarı eşiğinin altına inince yöneticilere otomatik bildirim + e-posta gider. Her başarılı gönderimde otomatik 1 düşer.</p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Kalan Kontör</Label>
                <Input data-testid="edoc-v2-kontor-balance" type="number" min="0" value={kontor.balance}
                  onChange={(e) => setKontor((k) => ({ ...k, balance: e.target.value }))} placeholder="örn. 500" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Uyarı Eşiği</Label>
                <Input data-testid="edoc-v2-kontor-threshold" type="number" min="0" value={kontor.threshold}
                  onChange={(e) => setKontor((k) => ({ ...k, threshold: e.target.value }))} placeholder="örn. 100" className="mt-1" />
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button data-testid="edoc-v2-kontor-save" size="sm" onClick={saveKontor} disabled={!!busy} className="bg-slate-800 text-white hover:bg-slate-700">
                {busy === "kontor" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Kontör Kaydet
              </Button>
              <Button data-testid="edoc-v2-kontor-query" size="sm" variant="outline" onClick={queryBalance} disabled={!!busy}>
                {busy === "balance" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Bakiye Sorgula
              </Button>
            </div>
            {usage && (
              <div className="mt-3" data-testid="edoc-v2-kontor-usage">
                <p className="text-[11px] font-semibold text-slate-500">Son 30 gün — gönderim / tüketim{usage.total > 0 ? ` (toplam ${usage.total})` : ""}</p>
                {usage.total === 0 ? (
                  <p className="mt-0.5 text-[11px] text-slate-400">Son 30 günde bu ortamda gönderim yok. Her başarılı gönderim 1 kontör düşer.</p>
                ) : (
                  <div className="mt-1 flex h-14 items-end gap-[2px]">
                    {(() => {
                      const mx = Math.max(1, ...usage.days.map((d) => d.count));
                      return usage.days.map((d, i) => (
                        <div key={i} title={`${d.date}: ${d.count} gönderim`} className="flex-1 rounded-t bg-brand-green"
                          style={{ height: `${Math.max(4, (d.count / mx) * 100)}%`, opacity: d.count ? 0.85 : 0.18 }} />
                      ));
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {testResult && (
          <div data-testid="edoc-v2-test-result" className={`mt-3 rounded-md p-3 ring-1 ring-inset ${testResult.ok ? "bg-emerald-50 ring-emerald-200" : "bg-red-50 ring-red-200"}`}>
            <p className={`mb-2 text-sm font-bold ${testResult.ok ? "text-emerald-700" : "text-red-700"}`}>{testResult.summary}</p>
            <div className="space-y-1">
              {(testResult.steps || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {s.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-red-600" />}
                  <span className="font-medium text-slate-700">{s.step}</span>
                  <span className="text-slate-400">— {s.note}</span>
                </div>
              ))}
            </div>
            {testResult.correlation_id && <p className="mt-2 font-mono text-[10px] text-slate-400">correlation: {testResult.correlation_id}</p>}
          </div>
        )}
      </>)}
    </div>
  );
}
